import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Trash2, X } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

import * as brandsApi from "@/api/brands";
import * as promptsApi from "@/api/prompts";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { extractErrorMessage } from "@/lib/api-errors";
import { ROLES, ROLE_LABELS } from "@/lib/roles";
import type { BrandSignature, BrandView, PromptView, SignatureTone } from "@/types/api";

const TONES: SignatureTone[] = ["warm", "serious", "playful"];
const TONE_LABELS: Record<SignatureTone, string> = {
  warm: "тёплый",
  serious: "серьёзный",
  playful: "игривый",
};

interface FormState {
  key: string;
  display_name: string;
  mp_aliases: string[];
  brand_context: string;
  signatures: BrandSignature[];
  signature_required: boolean;
  is_enabled: boolean;
}

const EMPTY: FormState = {
  key: "",
  display_name: "",
  mp_aliases: [],
  brand_context: "",
  signatures: [],
  signature_required: true,
  is_enabled: true,
};

export function BrandsPage(): React.ReactElement {
  const qc = useQueryClient();
  const { data: brands = [], isLoading } = useQuery({
    queryKey: ["brands"],
    queryFn: brandsApi.listBrands,
  });
  const { data: prompts = [] } = useQuery({
    queryKey: ["prompts"],
    queryFn: promptsApi.listPrompts,
  });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [aliasDraft, setAliasDraft] = useState("");

  const invalidate = () => qc.invalidateQueries({ queryKey: ["brands"] });
  const reset = () => {
    setForm(EMPTY);
    setEditingId(null);
    setAliasDraft("");
  };

  const createMut = useMutation({
    mutationFn: brandsApi.createBrand,
    onSuccess: () => {
      invalidate();
      reset();
      toast.success("Бренд создан");
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Ошибка создания")),
  });

  const updateMut = useMutation({
    mutationFn: (args: { id: number; body: brandsApi.BrandUpdate }) =>
      brandsApi.updateBrand(args.id, args.body),
    onSuccess: () => {
      invalidate();
      reset();
      toast.success("Бренд обновлён");
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Ошибка обновления")),
  });

  const deleteMut = useMutation({
    mutationFn: brandsApi.deleteBrand,
    onSuccess: () => {
      invalidate();
      toast.success("Бренд удалён");
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Ошибка удаления")),
  });

  const startEdit = (b: BrandView) => {
    setEditingId(b.id);
    setAliasDraft("");
    setForm({
      key: b.key,
      display_name: b.display_name,
      mp_aliases: [...b.mp_aliases],
      brand_context: b.brand_context ?? "",
      signatures: b.signatures.map((s) => ({ ...s })),
      signature_required: b.signature_required,
      is_enabled: b.is_enabled,
    });
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const signatures = form.signatures.filter((s) => s.text.trim());
    if (editingId) {
      updateMut.mutate({
        id: editingId,
        body: {
          display_name: form.display_name,
          mp_aliases: form.mp_aliases,
          brand_context: form.brand_context || null,
          signatures,
          signature_required: form.signature_required,
          is_enabled: form.is_enabled,
        },
      });
    } else {
      createMut.mutate({
        key: form.key,
        display_name: form.display_name,
        mp_aliases: form.mp_aliases,
        brand_context: form.brand_context || null,
        signatures,
        signature_required: form.signature_required,
        is_enabled: form.is_enabled,
      });
    }
  };

  // Alias chip editor helpers
  const addAlias = () => {
    const v = aliasDraft.trim();
    if (v && !form.mp_aliases.includes(v)) {
      setForm({ ...form, mp_aliases: [...form.mp_aliases, v] });
    }
    setAliasDraft("");
  };
  const removeAlias = (a: string) =>
    setForm({ ...form, mp_aliases: form.mp_aliases.filter((x) => x !== a) });

  // Signature editor helpers
  const addSignature = () =>
    setForm({ ...form, signatures: [...form.signatures, { text: "", tone: "warm" }] });
  const updateSignature = (i: number, patch: Partial<BrandSignature>) =>
    setForm({
      ...form,
      signatures: form.signatures.map((s, idx) => (idx === i ? { ...s, ...patch } : s)),
    });
  const removeSignature = (i: number) =>
    setForm({ ...form, signatures: form.signatures.filter((_, idx) => idx !== i) });

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Бренды</h1>
        <p className="text-sm text-muted-foreground">
          Соответствие брендов с маркетплейсов внутреннему бренду: алиасы названий,
          контекст для генерации, подписи и привязка промптов по ролям.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {editingId ? "Редактирование бренда" : "Новый бренд"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="brand-key">Ключ</Label>
                <Input
                  id="brand-key"
                  placeholder="omegavita"
                  value={form.key}
                  onChange={(e) => setForm({ ...form, key: e.target.value })}
                  disabled={!!editingId}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="brand-name">Название</Label>
                <Input
                  id="brand-name"
                  placeholder="OmegaVita"
                  value={form.display_name}
                  onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Алиасы на маркетплейсах</Label>
              <div className="flex flex-wrap gap-1.5">
                {form.mp_aliases.map((a) => (
                  <span
                    key={a}
                    className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 text-xs"
                  >
                    {a}
                    <button
                      type="button"
                      onClick={() => removeAlias(a)}
                      aria-label={`Удалить ${a}`}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="size-3" />
                    </button>
                  </span>
                ))}
              </div>
              <Input
                placeholder="Добавьте алиас и нажмите Enter"
                value={aliasDraft}
                onChange={(e) => setAliasDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addAlias();
                  }
                }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="brand-context">Контекст бренда</Label>
              <Textarea
                id="brand-context"
                className="min-h-[100px]"
                placeholder="Тон общения, позиционирование, что упоминать в ответах…"
                value={form.brand_context}
                onChange={(e) => setForm({ ...form, brand_context: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Подписи</Label>
              <div className="space-y-2">
                {form.signatures.map((s, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      className="flex-1"
                      placeholder="Текст подписи"
                      value={s.text}
                      onChange={(e) => updateSignature(i, { text: e.target.value })}
                    />
                    <select
                      className="h-10 rounded-md border border-input bg-background px-2 text-sm"
                      value={s.tone}
                      onChange={(e) =>
                        updateSignature(i, { tone: e.target.value as SignatureTone })
                      }
                    >
                      {TONES.map((t) => (
                        <option key={t} value={t}>
                          {TONE_LABELS[t]}
                        </option>
                      ))}
                    </select>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={() => removeSignature(i)}
                      aria-label="Удалить подпись"
                    >
                      <Trash2 className="size-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button type="button" size="sm" variant="outline" onClick={addSignature}>
                Добавить подпись
              </Button>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.signature_required}
                onChange={(e) =>
                  setForm({ ...form, signature_required: e.target.checked })
                }
              />
              Подпись обязательна
            </label>
            {!form.signature_required && form.signatures.filter((s) => s.text.trim()).length === 0 && (
              <p className="text-xs text-muted-foreground -mt-1">
                Ответы генерируются без подписи — просто по промпту бренда.
              </p>
            )}

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_enabled}
                onChange={(e) => setForm({ ...form, is_enabled: e.target.checked })}
              />
              Включён
            </label>

            <div className="flex gap-2">
              <Button type="submit" disabled={createMut.isPending || updateMut.isPending}>
                {editingId ? "Сохранить" : "Создать"}
              </Button>
              {editingId && (
                <Button type="button" variant="ghost" onClick={reset}>
                  Отмена
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="space-y-3">
        <h2 className="text-lg font-semibold">Бренды</h2>
        {isLoading && <p className="text-sm text-muted-foreground">Загрузка…</p>}
        {!isLoading && brands.length === 0 && (
          <p className="text-sm text-muted-foreground">Брендов ещё нет.</p>
        )}
        <ul className="space-y-2">
          {brands.map((b) => (
            <li key={b.id} className="space-y-3 rounded-md border p-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2 font-medium">
                    {b.display_name}
                    <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                      {b.key}
                    </span>
                    {b.is_enabled ? (
                      <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-800 dark:bg-green-950 dark:text-green-300">
                        включён
                      </span>
                    ) : (
                      <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                        выключен
                      </span>
                    )}
                  </div>
                  {b.mp_aliases.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {b.mp_aliases.map((a) => (
                        <span
                          key={a}
                          className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground"
                        >
                          {a}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground">
                    {ROLES.map((r) => (
                      <span key={r} className="mr-3">
                        {ROLE_LABELS[r]}: {b.links[r] ? "✓" : "—"}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="ghost" onClick={() => startEdit(b)}>
                    <Pencil className="size-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      if (window.confirm(`Удалить бренд «${b.display_name}»?`))
                        deleteMut.mutate(b.id);
                    }}
                  >
                    <Trash2 className="size-4 text-destructive" />
                  </Button>
                </div>
              </div>

              <BrandLinks brand={b} prompts={prompts} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function BrandLinks({
  brand,
  prompts,
}: {
  brand: BrandView;
  prompts: PromptView[];
}): React.ReactElement {
  const qc = useQueryClient();
  const linkMut = useMutation({
    mutationFn: (body: brandsApi.BrandLinksUpdate) =>
      brandsApi.updateBrandLinks(brand.id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["brands"] });
      toast.success("Привязка обновлена");
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Ошибка привязки промпта")),
  });

  return (
    <div className="grid gap-2 border-t pt-3 sm:grid-cols-3">
      {ROLES.map((role) => {
        const options = prompts.filter((p) => p.role === role);
        const current = brand.links[role]?.prompt_id ?? "";
        return (
          <div key={role} className="space-y-1">
            <Label className="text-xs text-muted-foreground">{ROLE_LABELS[role]}</Label>
            <select
              className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              value={current}
              disabled={linkMut.isPending}
              onChange={(e) => {
                const val = e.target.value;
                linkMut.mutate({ [role]: val === "" ? null : Number(val) });
              }}
            >
              <option value="">— не задано —</option>
              {options.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
          </div>
        );
      })}
    </div>
  );
}
